/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { formatCurrency } from '@sportninja/common/currency'
import colors from '@sportninja/common/constants/appColors'
import type { PlayerPoolEffect } from '@sportninja/common/services/teamBalance'
import Price from 'src/components/ts-components/Price'
import {
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'
import { dollarsToMinor } from '../utils/balanceMath'
import { balanceLabel, balanceText } from '../utils/balanceI18n'

type OfficialEffectListProps = {
  /** What is about to happen, in a sentence. */
  intro: string
  effect: PlayerPoolEffect
  currencyCode: string
  /** Set when the effect was re-read because the sheet moved since the first look. */
  changed?: boolean
}

/**
 * Sep 23 — what a change to a team official's line does to the UNINVOICED player
 * shares, read before it is confirmed: each share's before and after, and anything
 * that becomes Unallocated. The figures are the server's preview; nothing is
 * computed here.
 */
const OfficialEffectList = ({
  intro,
  effect,
  currencyCode,
  changed = false,
}: OfficialEffectListProps) => {
  const freedMinor = effect.freed_to_unallocated_cents

  return (
    <div css={bodyStyle} data-testid='official-effect'>
      {changed ? (
        <p css={noticeStyle} data-testid='official-effect-changed'>
          {balanceText('common:balanceOfficialPreviewChanged')}
        </p>
      ) : null}
      <p css={introStyle}>{intro}</p>
      {effect.lines.length > 0 ? (
        <table css={tableStyle}>
          <thead>
            <tr>
              <th>{balanceLabel('common:balanceColPlayer')}</th>
              <th css={numericStyle}>
                {balanceLabel('common:balanceOfficialEffectBefore')}
              </th>
              <th css={numericStyle}>
                {balanceLabel('common:balanceOfficialEffectAfter')}
              </th>
            </tr>
          </thead>
          <tbody>
            {effect.lines.map((line) => (
              <tr
                key={line.slot_id}
                data-testid={`official-effect-line-${line.slot_id}`}
              >
                <td>
                  {line.name ?? balanceLabel('common:balanceSlotTypePlayer')}
                </td>
                <td css={numericStyle}>
                  <Price
                    amount={dollarsToMinor(line.before)}
                    currency={currencyCode}
                  />
                </td>
                <td css={[numericStyle, afterStyle]}>
                  <Price
                    amount={dollarsToMinor(line.after)}
                    currency={currencyCode}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {effect.lines.length === 0 &&
      effect.mode === 'none' &&
      freedMinor === 0 ? (
        <p css={noteStyle} data-testid='official-effect-none'>
          {balanceText('common:balanceOfficialEffectNone')}
        </p>
      ) : null}
      {effect.mode === 'resplit' ? (
        <p css={noteStyle}>
          {balanceText('common:balanceOfficialEffectResplit')}
        </p>
      ) : null}
      {freedMinor > 0 ? (
        <p css={noteStyle} data-testid='official-effect-unallocated'>
          {balanceText('common:balanceOfficialNowUnallocated', {
            amount: formatCurrency({ amount: freedMinor, currencyCode }),
          })}
        </p>
      ) : null}
    </div>
  )
}

export default OfficialEffectList

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`

const introStyle = css`
  margin: 0;
  ${paragraphSmall};
  color: ${colors.NEUTRAL_0};
`

const noticeStyle = css`
  margin: 0;
  ${paragraphSmall};
  color: ${colors.WARNING_300};
`

const noteStyle = css`
  margin: 0;
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
`

/** The same ruled table the Send Invoice preview uses, so the two read alike. */
const tableStyle = css`
  width: 100%;
  border-collapse: collapse;
  ${paragraphSmall};

  th,
  td {
    padding: 8px 4px;
    border-bottom: 1px solid ${colors.SECONDARY_100};
    color: ${colors.NEUTRAL_0};
    text-align: left;
  }

  th {
    ${paragraphXSmall};
    ${weightSemiBold};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${colors.NEUTRAL_50};
  }
`

const numericStyle = css`
  text-align: right !important;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const afterStyle = css`
  ${weightSemiBold};
`
